import * as React from 'react';

import Menu from 'components/Menu';
import Glyph from 'components/Glyph';
import Button, { Props as ButtonProps } from 'components/Button';
import MenuItem from 'components/MenuItem';
import MenuButton from 'components/MenuButton';
import Text from 'components/Text';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Tooltip from 'components/Tooltip';
import LayerTrigger from 'components/LayerTrigger';
import Row from 'components/Row';

type Integration = {
  providerKey: string;
  isACIIntegrated: boolean;
  title: string;
  iconURL: string;
  iconFileUUID: string;
  productShortDescription: string;
  productLongDescription: string;
  adminCustomizedIntegrationSetup: any;
  LTI: any;
};

export type Props = {
  onActionUnauthorized: () => void;
  organizationUUID: string;
  canEdit: boolean;
  integration: Integration;
  isToggleable?: boolean;
  isEnabled?: boolean;
  toggleClickHandler?: () => void;
  hasDistrictOverride?: boolean;
};

const Enabled = () => <span className="AdminIntegratedToolsView-enabled">Enabled</span>;

type ATPIToggleButtonProps = { integrationEnabled?: boolean } & ButtonProps;
const ATPIToggleButton = ({ integrationEnabled, ...props }: ATPIToggleButtonProps) => (
  <Button size="xsmall" level={integrationEnabled ? 'secondary' : 'primary'} {...props}>
    {integrationEnabled ? 'Disable' : 'Enable'}
  </Button>
);

const DistrictOverridenATPIToggleButton = (props: ButtonProps) => (
  <LayerTrigger layer={<Tooltip>Enabled by your district</Tooltip>} delay={50} placement="top">
    <div>
      <ATPIToggleButton integrationEnabled disabled {...props} />
    </div>
  </LayerTrigger>
);

const AdminIntegration = ({
  integration,
  canEdit,
  isEnabled,
  hasDistrictOverride,
  isToggleable,
  toggleClickHandler,
  organizationUUID,
  onActionUnauthorized,
}: Props) => {
  const { isACIIntegrated, providerKey } = integration;
  const isLTIIntegrated = integration.LTI != null;

  const setupButtonProps = (type: string) =>
    canEdit
      ? { to: `/install_${type}/${organizationUUID}/${providerKey}` }
      : { onClick: onActionUnauthorized };
  const [ACIButtonProps, LTIButtonProps] = ['aci', 'lti'].map(setupButtonProps);

  const actions = [];

  if (isToggleable) {
    actions.push(
      hasDistrictOverride ? (
        <DistrictOverridenATPIToggleButton key={`admin-integration-${providerKey}`} />
      ) : (
        <ATPIToggleButton
          integrationEnabled={isEnabled}
          onClick={canEdit ? toggleClickHandler : onActionUnauthorized}
          key={`admin-integration-${providerKey}`}
        />
      )
    );
  } else if (!isACIIntegrated) {
    actions.push(
      <Button key="aci" size="xsmall" level="primary" {...ACIButtonProps}>
        Set up
      </Button>
    );
  } else if (!isLTIIntegrated) {
    actions.push(
      <Button key="lti" size="xsmall" level={'primary'} {...LTIButtonProps}>
        Set up LTI
      </Button>
    );
  }

  if (isACIIntegrated || isLTIIntegrated) {
    actions.push(
      <MenuButton
        key="menu"
        size="xsmall"
        level="secondary"
        alignment="right"
        placement="bottom"
        menu={
          <Menu>
            {isACIIntegrated && (
              <MenuItem {...ACIButtonProps} menuKey={providerKey + '_aci'}>
                Manage app
              </MenuItem>
            )}
            {isLTIIntegrated && (
              <MenuItem {...LTIButtonProps} menuKey={providerKey + '_lti'}>
                Manage LTI
              </MenuItem>
            )}
          </Menu>
        }>
        <Glyph name="ellipses" solo />
      </MenuButton>
    );
  }

  return (
    <Row size="xsmall">
      <div className="AdminIntegratedToolsView-admin">
        <div className="AdminIntegratedToolsView-admin-info">
          <MediaObject gutter="medium" center>
            <div className="AdminIntegratedToolsView-admin-info-logo">
              <Image src={integration.iconURL} />
            </div>
            <div>
              <div>
                <Text weight="semibold" size="medium">
                  {integration.title}
                </Text>
                {isEnabled || isACIIntegrated ? <Enabled /> : null}
              </div>
              <Text element="div" color="thunder">
                {integration.productShortDescription}
              </Text>
            </div>
          </MediaObject>
        </div>
        <div className="AdminIntegratedToolsView-admin-buttons">{actions}</div>
      </div>
    </Row>
  );
};

export default AdminIntegration;
